import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard } from 'react-native';
import {
    ControlledInput,
    ControlledMultilineInput,
    Form,
    USE_FORM_MODE_DEFAULTS,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useFlow } from '../../common/providers/FlowProvider';
import { useFlowRouter } from '../../common/providers/FlowRouter';
import { BaseStep } from '../../common/Step';
import {
    centsToFormattedDollars,
    formatCurrencyInputOnChange,
    formattedDollarsToCents,
} from '../../common/util/currencyUtils';
import type { AddIncomeFlowSteps } from '../AddIncomeFlow';
import { type Income } from '../types/Income';

const otherIncomeDetailsSchema = z.object({
    monthlyTakeHome: z.string().min(3), // monthlyTakeHome is the formatted string, which includes a left padding of two characters: "$ ".
    additionalInformation: z.string().optional(),
});

type OtherIncomeDetailsSchema = z.infer<typeof otherIncomeDetailsSchema>;

export const OtherDetailsStep = () => {
    const { data } = useFlow<Income>();
    const { onContinue } = useFlowRouter<Income, AddIncomeFlowSteps>();

    const {
        register,
        control,
        handleSubmit,
        setFocus,
        formState: { errors, isValid },
    } = useForm<OtherIncomeDetailsSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(otherIncomeDetailsSchema),
        defaultValues: {
            monthlyTakeHome: centsToFormattedDollars(data.monthlyTakeHomeCents),
            additionalInformation: data.additionalInformation ?? '',
        },
    });

    const onSubmit: SubmitHandler<OtherIncomeDetailsSchema> = async ({
        monthlyTakeHome,
        additionalInformation,
    }: OtherIncomeDetailsSchema) => {
        const incomeData = {
            monthlyTakeHomeCents: formattedDollarsToCents(monthlyTakeHome),
            additionalInformation,
        };

        await onContinue(incomeData);
    };

    return (
        <BaseStep
            onContinue={handleSubmit(onSubmit)}
            isContinueDisabled={!isValid}
        >
            <Form onSubmit={handleSubmit(onSubmit)} marginBottom={12}>
                <YStack gap={12}>
                    <ControlledInput
                        label="Monthly amount"
                        name="monthlyTakeHome"
                        type="numeric"
                        inputMode="numeric"
                        control={control}
                        register={register}
                        errors={errors}
                        enterKeyHint="next"
                        formatValueOnChange={formatCurrencyInputOnChange}
                        onSubmitEditing={() => {
                            setFocus('additionalInformation');
                        }}
                    />
                    <ControlledMultilineInput
                        label="Additional information (optional)"
                        name="additionalInformation"
                        control={control}
                        register={register}
                        errors={errors}
                        enterKeyHint="done"
                        blurOnSubmit={true}
                        onSubmitEditing={() => {
                            Keyboard.dismiss();
                        }}
                    />
                </YStack>
            </Form>
        </BaseStep>
    );
};
